import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { useTypedParams } from '@sorare/routing';
import { Horizontal } from '@sorare/core/src/atoms/layout/flex';
import { LoadingIndicator } from '@sorare/core/src/atoms/loader/LoadingIndicator';
import { Section } from '@sorare/core/src/components/layout/MainAsideLayout';
import { Cost } from '@sorare/core/src/components/player/Cost';
import { Header } from '@sorare/core/src/components/seo/Header';
import { Departures } from '@sorare/core/src/components/seo/Team/Departures';
import { Squad as DumbSquad } from '@sorare/core/src/components/seo/Team/Squad';
import { ANY_SPORT_PLAYERS_SLUG } from '@sorare/core/src/constants/__generated__/routes';
import { useQuery } from '@sorare/core/src/hooks/graphql/useQuery';
import { useTitleAndDescription } from '@sorare/core/src/hooks/useTitleAndDescription';
import { generateSportPath } from '@sorare/core/src/lib/routing/generateSportPath';
import { metadatas } from '@sorare/core/src/lib/seo/nba';
import { desktopAndAbove } from '@sorare/core/src/style/mediaQuery';
import { InjuryTag } from '@sorare/marketplace/src/components/player/InjuryTag';

import type {
  NbaTeamOverviewQuery,
  NbaTeamOverviewQueryVariables,
} from './__generated__/page.graphql.ts';
import type { PageParams } from './__generated__/routeParams.ts';

const Layout = styled.div`
  @media ${desktopAndAbove} {
    display: grid;
    grid-template-areas: 'main side';
    grid-template-columns: 1fr 400px;
  }
`;

const NBA_TEAM_OVERVIEW_QUERY = gql`
  query NbaTeamOverviewQuery($slug: String!) {
    team(slug: $slug) {
      slug
      name
      pictureSecondaryUrl
      styleGuideColor
      anyActivePlayers {
        nodes {
          slug
          sport
          displayName
          shirtNumber
          avatarPictureUrl
          age
          country {
            slug
            code
            threeLetterCode
          }
          lastTenPlayedSo5AverageScore: averageScore(
            type: LAST_TEN_PLAYED_SO5_AVERAGE_SCORE
          )
          ...InjuryTag_anyPlayer
        }
      }
      ...Header_subscription
      ...Departures_TeamInterface
    }
  }
  ${InjuryTag.fragments.anyPlayer}
  ${Header.fragments.subscription}
  ${Departures.fragments.TeamInterface}
` as TypedDocumentNode<NbaTeamOverviewQuery, NbaTeamOverviewQueryVariables>;

const CostContainer = styled.div`
  display: flex;
  justify-content: end;
`;

const Overview = () => {
  const { slug } = useTypedParams<PageParams>();
  const { data, loading } = useQuery(NBA_TEAM_OVERVIEW_QUERY, {
    variables: { slug },
  });

  useTitleAndDescription(
    metadatas.team.title,
    metadatas.team.description,
    {
      team: data?.team.name,
    },
    {
      img: data?.team.pictureSecondaryUrl,
    }
  );

  if (loading) return <LoadingIndicator fullHeight />;
  if (!data?.team) return null;

  const { team } = data;

  return (
    <Section>
      <Header
        id={team.slug}
        variant="team"
        displayName={team.name}
        pictureUrl={team.pictureSecondaryUrl}
        color={team.styleGuideColor}
        subscription={team}
      />
      <Layout>
        <DumbSquad
          displayName={team.name}
          stats={['injury', 'shirtNumber', 'l10']}
          headers={{
            injury: '',
            shirtNumber: '#',
            l10: (
              <FormattedMessage
                id="Seo.Teams.Slug.Overview.l10"
                defaultMessage="L10"
              />
            ),
          }}
          groups={[
            {
              players: [...team.anyActivePlayers.nodes]

                .sort(
                  (a, b) =>
                    (b.lastTenPlayedSo5AverageScore || 0) -
                    (a.lastTenPlayedSo5AverageScore || 0)
                )
                .map(player => ({
                  ...player,
                  country: {
                    code: player.country.code.toLowerCase(),
                    threeLetterCode: player.country.threeLetterCode,
                  },
                  pictureUrl: player.avatarPictureUrl,
                  to: generateSportPath(ANY_SPORT_PLAYERS_SLUG, {
                    params: { slug: player.slug },
                    sport: player.sport,
                  }),
                  stats: {
                    injury: (
                      <Horizontal center>
                        {!!player.activeInjuries.length && (
                          <InjuryTag player={player} />
                        )}
                      </Horizontal>
                    ),
                    shirtNumber: `#${player.shirtNumber}`,
                    l10: (
                      <CostContainer>
                        <Cost score={player.lastTenPlayedSo5AverageScore} />
                      </CostContainer>
                    ),
                  },
                })),
            },
          ]}
        />
        <Departures team={team} />
      </Layout>
    </Section>
  );
};

export default Overview;
